"use client";

/**
 * The tabs under a listing (feedback ticket 65, 2026-11-01; owner: one
 * block with tabs, not three stacked sections): "Bu kompleksdə" (the
 * residence's other listings), "Yaxınlıqda" (the location-first similar
 * list) and "Görünüşcə oxşar" (lookalikes in the same market). The listing
 * page (seo/ListingAroundTabs) and the drawer (ListingAroundSection) share
 * this bar: the segmented track of the history page's sale / rent switch
 * and BudgetAreas, the picked segment in the faint brand tint
 * (SEGMENT_ON). A tab with nothing in it is never passed in; a count shows
 * only where it is a true count. One row that scrolls sideways when it
 * runs out of room, so the bar never changes height.
 */
import { useRef, type KeyboardEvent } from "react";
import { SEGMENT_OFF, SEGMENT_ON } from "@/lib/ui-classes";
import { haptic } from "@/lib/haptics";

export type AroundTab = "building" | "nearby" | "looks";

export interface AroundTabItem {
  id: AroundTab;
  label: string;
  /** A true count (the residence's other listings), else nothing. */
  count?: number | null;
}

/** The tab to show: the one picked while it still has listings, else the
 *  first one that has any. */
export function activeTab(picked: AroundTab | null, tabs: AroundTabItem[]): AroundTab | null {
  if (picked && tabs.some((x) => x.id === picked)) return picked;
  return tabs[0]?.id ?? null;
}

export default function AroundTabBar({
  tabs,
  value,
  onChange,
  idBase,
  label,
  compact = false,
}: {
  tabs: AroundTabItem[];
  value: AroundTab;
  onChange: (tab: AroundTab) => void;
  /** Ties each tab to its panel (`${idBase}-tab-…` / `${idBase}-panel-…`). */
  idBase: string;
  /** The tab list's accessible name. */
  label: string;
  /** The drawer's smaller segments. */
  compact?: boolean;
}) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  // Arrow keys move between the tabs (the WAI-ARIA tabs pattern).
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft" && e.key !== "Home" && e.key !== "End") return;
    e.preventDefault();
    const i = tabs.findIndex((x) => x.id === value);
    const next = e.key === "Home" ? 0 : e.key === "End" ? tabs.length - 1
      : (i + (e.key === "ArrowRight" ? 1 : -1) + tabs.length) % tabs.length;
    onChange(tabs[next].id);
    refs.current[next]?.focus();
  };
  return (
    <div
      role="tablist"
      aria-label={label}
      onKeyDown={onKey}
      className="flex w-fit max-w-full overflow-x-auto rounded-xl bg-gray-100 p-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    >
      {tabs.map((tab, i) => {
        const on = tab.id === value;
        return (
          <button
            key={tab.id}
            ref={(el) => { refs.current[i] = el; }}
            type="button"
            role="tab"
            id={`${idBase}-tab-${tab.id}`}
            aria-selected={on}
            aria-controls={`${idBase}-panel-${tab.id}`}
            tabIndex={on ? 0 : -1}
            onClick={() => { if (!on) { haptic(6); onChange(tab.id); } }}
            className={`inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
              compact ? "px-2.5 py-1 text-[13px]" : "px-3 py-1.5 text-sm"
            } ${on ? SEGMENT_ON : SEGMENT_OFF}`}
          >
            {tab.label}
            {tab.count != null && <span className="tabular-nums opacity-70">{tab.count}</span>}
          </button>
        );
      })}
    </div>
  );
}
